import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useSettings } from '../context/SettingsContext';
import Loader from './common/Loader';
import api from '../services/api';

const Layout = () => {
  const [toggled, setToggled] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { settings, loading } = useSettings();

  // Refresh user permissions on every app load
  useEffect(() => {
    const refreshUser = async () => {
      try {
        const response = await api.get('/user');
        const freshUser = response.data;
        localStorage.setItem('user', JSON.stringify(freshUser));
        window.dispatchEvent(new CustomEvent('profileUpdated', { detail: freshUser }));
      } catch (err) {
        // Silently fail — user will see stale permissions until next login
        console.warn('Could not refresh user permissions:', err.message);
      }
    };
    refreshUser();
  }, []);

  if (loading) return <Loader fullScreen message="Loading..." />;

  const handleToggle = () => {
    setToggled(!toggled);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className="d-flex">
      {/* Sidebar with toggle class */}
      <div className={`sidebar ${toggled ? 'show' : ''}`}>
        <Sidebar
          logo={settings.app_logo}
          collapsed={collapsed}
          toggleCollapse={toggleCollapse}
          onItemClick={() => setToggled(false)}
        />
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {toggled && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-50 d-lg-none"
          style={{ zIndex: 1045 }}
          onClick={handleToggle}
        ></div>
      )}

      {/* Page Content */}
      <div className={`main-content w-100 d-flex flex-column h-100 p-0 ${collapsed ? 'collapsed' : ''}`}>
        {/* Mobile Header Toggle */}
        <div className="top-header d-lg-none flex-shrink-0 d-flex align-items-center">
          <button className="btn btn-primary btn-icon" onClick={handleToggle}>
            <i className="bi bi-list fs-4"></i>
          </button>
          <div className="ms-3 d-flex align-items-center">
            <Link to="/" onClick={() => setToggled(false)}>
              <img
                src={settings.app_logo ? (settings.app_logo.startsWith('http') ? settings.app_logo : `https://lms.appy.pk/api${settings.app_logo.startsWith('/') ? '' : '/'}${settings.app_logo}`) : "/logo.svg"}
                alt="Logo"
                style={{ height: '32px', width: 'auto' }}
              />
            </Link>
          </div>
          <div className="ms-auto me-3">
            {(settings.academic_session || settings.academic_year) && (
              <span 
                className="badge bg-light text-secondary border px-3 py-2 rounded-pill fw-semibold"
                style={{ fontSize: '0.72rem' }}
              >
                <i className="bi bi-calendar-event text-primary me-1"></i>
                Session: {settings.academic_session || settings.academic_year}
              </span>
            )}
          </div>
        </div>

        {/* Floating Back to Super Admin Button */}
        {localStorage.getItem('superAdminToken') && (
          <button
            className="btn btn-dark position-fixed bottom-0 end-0 m-4 shadow-lg rounded-pill px-4 py-2 d-flex align-items-center gap-2"
            style={{ zIndex: 9999 }}
            onClick={async () => {
              const superAdminToken = localStorage.getItem('superAdminToken');
              let superAdminUser = localStorage.getItem('superAdminUser');

              if (superAdminToken) {
                try {
                  // Restore token first
                  localStorage.setItem('token', superAdminToken);
                  localStorage.removeItem('superAdminToken');

                  // If user details are missing, fetch them
                  if (!superAdminUser) {
                    const response = await axios.get(`${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}/api/user`, {
                      headers: { Authorization: `Bearer ${superAdminToken}` }
                    });
                    // Handle potential 'data' wrapper from API resources
                    const userData = response.data.data || response.data;
                    superAdminUser = JSON.stringify(userData);
                  }

                  // Restore user details
                  if (superAdminUser) {
                    localStorage.setItem('user', superAdminUser);
                    localStorage.removeItem('superAdminUser');
                    window.location.href = '/super-admin';
                  } else {
                    // Fallback to login if fetch failed
                    window.location.href = '/login';
                  }
                } catch (error) {
                  console.error("Failed to restore Super Admin session", error);
                  window.location.href = '/login';
                }
              }
            }}
          >
            <i className="bi bi-arrow-left-circle-fill fs-4 text-warning"></i>
            <span className="fw-bold">Back to Super Admin</span>
          </button>
        )}



        {/* Scrollable Main Content */}
        <div className="flex-grow-1 overflow-auto p-4">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
